import { CROPS, type CropId, growingStages, isInSeason } from '@/data/crops';
import type { Season } from '@/time/clock';

/**
 * Tilled ground and what is growing in it.
 *
 * Sparse: only tiles the player has actually worked exist, so an empty farm
 * costs nothing and a save stays small. Keyed by tile coordinate rather than
 * stored in the tilemap, because the tilemap is static content and this is
 * playthrough state — mixing them would mean the map could not be edited
 * without invalidating saves.
 */

export interface Plot {
  /** Whether the soil has been hoed. Untilled ground cannot be planted. */
  tilled: boolean;
  /** Day index the plot was last watered, or null if never. */
  wateredOnDay: number | null;
  crop: PlantedCrop | null;
  /**
   * Condition of the soil, 0..MAX_QUALITY.
   *
   * Rises with consistent watering and decays with neglect, so a bed that has
   * been tended for a season is measurably better than one broken yesterday.
   * Rewards the routine rather than gating on it: poor soil still grows crops,
   * it just yields less.
   */
  quality: number;
}

export const MAX_QUALITY = 100;
/** Gained per watered day. Twelve days of care reaches the top tier. */
const QUALITY_PER_WATERED_DAY = 7;
/** Lost per dry day. Slower than the gain, so a missed day is not a setback. */
const QUALITY_PER_DRY_DAY = 3;

export type SoilTier = 'poor' | 'good' | 'rich';

export function soilTier(quality: number): SoilTier {
  if (quality >= 67) return 'rich';
  if (quality >= 34) return 'good';
  return 'poor';
}

/** Extra produce from well-kept ground. */
export function yieldBonus(tier: SoilTier): number {
  return tier === 'rich' ? 1 : 0;
}

export interface PlantedCrop {
  readonly id: CropId;
  /** 0-based growing stage; equal to the stage count once ripe. */
  stage: number;
  /** Watered days accumulated at the current stage. */
  daysInStage: number;
}

export interface PlotSave {
  readonly x: number;
  readonly y: number;
  readonly tilled: boolean;
  readonly wateredOnDay: number | null;
  readonly crop: { readonly id: string; readonly stage: number; readonly daysInStage: number } | null;
  readonly quality: number;
}

export type PlantResult = 'planted' | 'not-tilled' | 'occupied' | 'wrong-season';

export class Farmland {
  private readonly plots = new Map<string, Plot>();

  get size(): number {
    return this.plots.size;
  }

  at(x: number, y: number): Plot | undefined {
    return this.plots.get(key(x, y));
  }

  /** Hoe a tile. Returns false if it was already tilled. */
  till(x: number, y: number): boolean {
    const existing = this.at(x, y);
    if (existing?.tilled) return false;
    // Re-tilling ground that was already worked keeps its condition, so
    // clearing a spent crop does not undo a season of care.
    this.plots.set(key(x, y), {
      tilled: true,
      wateredOnDay: null,
      crop: null,
      quality: existing?.quality ?? 0,
    });
    return true;
  }

  /**
   * Clear a tile back to nothing.
   *
   * Tilling over a growing crop would be a silent way to lose work, so this is
   * only reachable deliberately.
   */
  clear(x: number, y: number): void {
    this.plots.delete(key(x, y));
  }

  plant(x: number, y: number, cropId: CropId, season: Season): PlantResult {
    const plot = this.at(x, y);
    if (!plot?.tilled) return 'not-tilled';
    if (plot.crop) return 'occupied';
    if (!isInSeason(CROPS[cropId], season)) return 'wrong-season';

    plot.crop = { id: cropId, stage: 0, daysInStage: 0 };
    return 'planted';
  }

  /** Water a tilled tile. Returns false if there is nothing to water. */
  water(x: number, y: number, dayIndex: number): boolean {
    const plot = this.at(x, y);
    if (!plot?.tilled) return false;
    if (plot.wateredOnDay === dayIndex) return false;
    plot.wateredOnDay = dayIndex;
    return true;
  }

  /** Water every tilled plot at once, for rain. */
  waterAll(dayIndex: number): void {
    for (const plot of this.plots.values()) {
      if (plot.tilled) plot.wateredOnDay = dayIndex;
    }
  }

  isWatered(x: number, y: number, dayIndex: number): boolean {
    return this.at(x, y)?.wateredOnDay === dayIndex;
  }

  isRipe(x: number, y: number): boolean {
    const crop = this.at(x, y)?.crop;
    return crop !== null && crop !== undefined && crop.stage >= growingStages(CROPS[crop.id]);
  }

  /**
   * Pick a ripe crop. Returns what came off the plant, or null if it was not
   * ready. Crops that bear repeatedly drop back a stage instead of clearing.
   */
  harvest(x: number, y: number): { cropId: CropId; amount: number; tier: SoilTier } | null {
    const plot = this.at(x, y);
    const crop = plot?.crop;
    if (!plot || !crop || !this.isRipe(x, y)) return null;

    const definition = CROPS[crop.id];

    if (definition.regrowToStage === undefined) {
      plot.crop = null;
    } else {
      crop.stage = definition.regrowToStage;
      crop.daysInStage = 0;
    }

    const tier = soilTier(plot.quality);
    return {
      cropId: definition.id,
      amount: definition.yieldAmount + yieldBonus(tier),
      tier,
    };
  }

  /**
   * Roll the farm forward one day.
   *
   * Only tiles watered on `previousDay` make progress: watering is the daily
   * ritual the whole loop hangs on, and a crop that grows regardless would
   * make it pointless. Nothing dies from neglect — this is a cosy game, and
   * losing a week of pumpkins to one missed evening is not the feeling.
   */
  advanceDay(previousDay: number, season: Season): void {
    for (const plot of this.plots.values()) {
      const wasWatered = plot.wateredOnDay === previousDay;
      // Dry soil first, so the visuals reset even where nothing is planted.
      plot.wateredOnDay = null;

      plot.quality = clamp(
        plot.quality + (wasWatered ? QUALITY_PER_WATERED_DAY : -QUALITY_PER_DRY_DAY),
        0,
        MAX_QUALITY,
      );

      const crop = plot.crop;
      if (!crop || !wasWatered) continue;

      const definition = CROPS[crop.id];
      // Out of season a plant holds where it is rather than dying, so a
      // late-summer tomato survives into autumn as a stump.
      if (!isInSeason(definition, season)) continue;

      const stages = growingStages(definition);
      if (crop.stage >= stages) continue;

      crop.daysInStage++;
      if (crop.daysInStage >= definition.stageDays[crop.stage]!) {
        crop.stage++;
        crop.daysInStage = 0;
      }
    }
  }

  toSave(): PlotSave[] {
    const saved: PlotSave[] = [];
    for (const [coordinate, plot] of this.plots) {
      const [x, y] = coordinate.split(',').map(Number) as [number, number];
      saved.push({
        x,
        y,
        tilled: plot.tilled,
        wateredOnDay: plot.wateredOnDay,
        crop: plot.crop
          ? { id: plot.crop.id, stage: plot.crop.stage, daysInStage: plot.crop.daysInStage }
          : null,
        quality: plot.quality,
      });
    }
    return saved;
  }

  /**
   * Rebuild from saved data, discarding anything unreadable.
   *
   * A single bad plot must not cost the player their whole farm, so entries are
   * validated individually rather than the array being rejected wholesale.
   */
  static fromSave(entries: unknown): Farmland {
    const farmland = new Farmland();
    if (!Array.isArray(entries)) return farmland;

    for (const entry of entries as PlotSave[]) {
      if (!entry || typeof entry.x !== 'number' || typeof entry.y !== 'number') continue;
      if (!Number.isInteger(entry.x) || !Number.isInteger(entry.y)) continue;

      const crop =
        entry.crop && typeof entry.crop.id === 'string' && entry.crop.id in CROPS
          ? {
              id: entry.crop.id as CropId,
              stage: clampStage(entry.crop.stage, entry.crop.id as CropId),
              daysInStage: Math.max(0, Math.floor(entry.crop.daysInStage) || 0),
            }
          : null;

      farmland.plots.set(key(entry.x, entry.y), {
        tilled: entry.tilled !== false,
        wateredOnDay:
          typeof entry.wateredOnDay === 'number' && Number.isFinite(entry.wateredOnDay)
            ? entry.wateredOnDay
            : null,
        crop,
        quality:
          typeof entry.quality === 'number' && Number.isFinite(entry.quality)
            ? clamp(Math.floor(entry.quality), 0, MAX_QUALITY)
            : 0,
      });
    }

    return farmland;
  }
}

function clampStage(stage: unknown, cropId: CropId): number {
  const max = growingStages(CROPS[cropId]);
  const value = typeof stage === 'number' && Number.isFinite(stage) ? Math.floor(stage) : 0;
  return Math.min(Math.max(0, value), max);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function key(x: number, y: number): string {
  return `${x},${y}`;
}
